import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { useOnComponentMount } from '../../../../../hooks/use-on-component-mount';
import { LossPreventionService } from '../../../../../services/loss-prevention-service';
import { messages } from '../messages';

import type { LossPreventionSettings } from '../../../../../services/loss-prevention-service';

type UseLossPreventionStateReturn = {
  settings: LossPreventionSettings | null;
  isLoading: boolean;
  isSaving: boolean;
  updateSetting: (path: string, value: unknown) => void;
  saveSettings: () => Promise<void>;
};

/**
 * Custom hook to manage loss prevention settings state
 * Handles fetching, local state management, and saving of settings
 */
export const useLossPreventionState = (): UseLossPreventionStateReturn => {
  const intl = useIntl();
  const [settings, setSettings] = useState<LossPreventionSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch settings on mount
  useOnComponentMount(() => {
    const fetchSettings = async () => {
      try {
        const data = await LossPreventionService.getSettings();
        setSettings(data);
      } catch {
        NotificationService.showNotification({
          content: intl.formatMessage(messages.loadError),
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  });

  /**
   * Update a setting value using dot notation path
   * Uses recursion to support arbitrary nesting depth
   * @example updateSetting('isEnabled', true)
   * @example updateSetting('unusualOrderTime.startHour', 5)
   * @example updateSetting('deeply.nested.property.path', value)
   */
  const updateSetting = useCallback((path: string, value: unknown) => {
    setSettings((prev) => {
      if (!prev) return prev;

      const keys = path.split('.');

      /**
       * Recursively updates a nested value in an object
       * @param obj - The object to update
       * @param pathKeys - Array of keys representing the path
       * @returns A new object with the updated value
       */
      const updateNested = (obj: Record<string, unknown>, pathKeys: string[]): Record<string, unknown> => {
        // Base case: only one key left, set the value
        if (pathKeys.length === 1) {
          return { ...obj, [pathKeys[0]]: value };
        }

        // Recursive case: navigate deeper into the object
        const [currentKey, ...remainingKeys] = pathKeys;
        const currentValue = obj[currentKey];

        return {
          ...obj,
          [currentKey]: updateNested(
            currentValue && typeof currentValue === 'object' ? (currentValue as Record<string, unknown>) : {},
            remainingKeys,
          ),
        };
      };

      return updateNested(prev as unknown as Record<string, unknown>, keys) as LossPreventionSettings;
    });
  }, []);

  /**
   * Save settings to backend
   */
  const saveSettings = useCallback(async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      await LossPreventionService.updateSettings(settings);

      NotificationService.showNotification({
        content: intl.formatMessage(messages.saveSuccess),
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
    } catch {
      NotificationService.showNotification({
        content: intl.formatMessage(messages.saveError),
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  }, [settings, intl]);

  return {
    settings,
    isLoading,
    isSaving,
    updateSetting,
    saveSettings,
  };
};
